import React, { useState, useEffect, useCallback } from 'react';
import { getStyles } from '../../theme/styles';
import { useTheme } from '@mui/material/styles';
import { InputLabel, MenuItem, Select, FormControl, SelectChangeEvent } from '@mui/material';
import provisionService from '../../services/ProvisionService';
import { AcademicYearDTO } from '@beable/roster-backend-client-v1';

interface AcademicYearSelectorProps {
  siteId: number;
  handleSelection: (academicYear: AcademicYearDTO | undefined) => void;
  academicYearId?: string;
  disabled?: boolean;
  className?: string;
  clearAcademicYear?: boolean;
  includePrevious?: boolean;
  variant?: 'filled' | 'outlined';
}

export default function AcademicYearSelector({ academicYearId, siteId, handleSelection, disabled, className, clearAcademicYear, includePrevious, variant }: AcademicYearSelectorProps) {
  const theme = useTheme();
  const styles = getStyles(theme);
  const [selectedAcademicYear, setSelectedAcademicYear] = useState<AcademicYearDTO | undefined>(undefined);
  const [academicYears, setAcademicYears] = useState<AcademicYearDTO[]>([]);

  const getAcademicYearById = (academicYearId: string) => {
    return academicYears.find(academicYear =>
      academicYearId && academicYear.academicYearId === academicYearId
    );
  }

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedAcademicYear = event.target && getAcademicYearById(event.target.value);
    if (selectedAcademicYear) {
      setSelectedAcademicYear(selectedAcademicYear);
      handleSelection(selectedAcademicYear);
    }
  }

  const memoOnSelect = useCallback(handleSelection, []);

  useEffect(() => {
    const fetchAcademicYears = async () => {
      const data = await provisionService.getAcademicYears(siteId);
      let returnedAcademicYears = data?.academicYears || [];

      if (includePrevious) {
        const lastYear = await provisionService.getPreviousAcademicYear(siteId);
        if (lastYear) {
          returnedAcademicYears = [lastYear, ...returnedAcademicYears];
        }
      }
        
      setAcademicYears(returnedAcademicYears);

      if (academicYearId && !data?.academicYears.find((a) => a.academicYearId === academicYearId)) {
        setSelectedAcademicYear(undefined);
        memoOnSelect(undefined);
      }
    };

    if (!siteId) {
      setAcademicYears([]);
      setSelectedAcademicYear(undefined);
      memoOnSelect(undefined);
   } else {
      fetchAcademicYears();
    }
  }, [siteId, memoOnSelect]);


  useEffect(() => {
    if (clearAcademicYear) {
      setSelectedAcademicYear(undefined);
    }
  }, [clearAcademicYear]);

  useEffect(() => {
    if (academicYearId && academicYears.length) {   
      const academicYear = academicYears.find(academicYear => academicYear.academicYearId === academicYearId);
      
      if (academicYear) {
        setSelectedAcademicYear(academicYear);
      }
    }

  }, [academicYears, academicYearId]);

  return (
    <FormControl className={className} sx={className ? undefined : styles.pFormControl} variant={ variant || 'standard' }>
      <InputLabel id='academicYearLabel' htmlFor='academicYearId'>Select Academic Year</InputLabel>
      <Select
        variant={ variant || 'standard' }
        value={selectedAcademicYear?.academicYearId || ''}
        onChange={(event: SelectChangeEvent<string>, child: React.ReactNode) => handleChange(event) }
        inputProps={{
          'name': 'academicYearId',
          'id': 'academicYearId',
          'data-testid': 'test-academicYearId',
          'role': 'option'
        }}
        disabled={academicYears.length < 1 || disabled}
        labelId="academicYearLabel">
        {
          academicYears.length &&
          academicYears.map((academicYear) => (
            <MenuItem key={academicYear.academicYearId} value={academicYear.academicYearId} role='option'>
              {academicYear.schoolYear}
            </MenuItem>
          ))
        }
        {
          siteId && !academicYears.length &&
          <MenuItem key={0} value={0} role='option'>
            No academic years found
          </MenuItem>
        }

      </Select>
    </FormControl>
  );
}
